import moment from "moment";
import { useCallback, useMemo, useState } from "react";

import { useIntl } from "../../../core/i18n/use-intl";
import { transactionSearchManager } from "../../../core/service/services";
import { useObservable } from "../../../utils/observable";
import type { Account } from "../../account/account";
import { getSettlementBalance } from "../../account/account";
import { TimeRange } from "../search/timerange";
import { JustificationFilter } from "../search/transaction-search-manager";
import { TransactionDirection } from "./transaction-filter";

export const useTransactionFilter = (account: Account | undefined | null) => {
  const timerangeSelection = useObservable(transactionSearchManager.timeRangeSelection);
  const amountRange = useObservable(transactionSearchManager.amountRange);
  const directionSelection = useObservable(transactionSearchManager.directionSelection);
  const justificationSelection = useObservable(transactionSearchManager.justificationSelection);

  const filters = useMemo(
    () => ({
      timerange: timerangeSelection,
      amountRange,
      currencyId: account?.currencyAlpha,
      // The API only accepts the "not yet done" side of these, hence 0 or undefined.
      ...(justificationSelection === JustificationFilter.TO_JUSTIFY && { isJustified: 0, isLocked: 0 }),
      ...(justificationSelection === JustificationFilter.TO_QUALIFY && { isQualified: 0, isLocked: 0 }),
      // Justification and qualification only concern expenses. Without this the API returns
      // every unjustified transaction, incoming money included, since a credit is never
      // justified. Same restriction as the counters in TransactionsLoader.
      ...(justificationSelection !== JustificationFilter.ALL && { direction: "debit" as const }),
    }),
    [timerangeSelection, amountRange, account, justificationSelection],
  );

  const { formattedAmountRange, hasAmountFilter } = useFormattedAmountRange(account);
  const hasDirectionFilter = directionSelection !== TransactionDirection.ALL;
  const hasJustificationFilter = justificationSelection !== JustificationFilter.ALL;
  const hasFilter =
    hasDirectionFilter ||
    timerangeSelection.timeRange !== TimeRange.Quarter ||
    hasAmountFilter ||
    hasJustificationFilter;

  return {
    filters,
    formattedAmountRange,
    hasFilter,
    hasDirectionFilter,
    directionFilter: directionSelection,
    hasDateCustomFilter: timerangeSelection.timeRange === TimeRange.Other,
    fromDate: timerangeSelection.bounds[0]?.toDate(),
    toDate: timerangeSelection.bounds[1]?.toDate(),
    hasAmountFilter,
    justificationFilter: justificationSelection,
    hasJustificationFilter,
  };
};
export const useFormattedAmountRange = (account: Account | undefined | null) => {
  const currency = account ? getSettlementBalance(account).currencyAlpha : null;
  const { formatNumber } = useIntl();
  const amountRange = useObservable(transactionSearchManager.amountRange);
  const hasAmountFilter = hasBounds(amountRange);
  const formattedAmountRange = useMemo(
    () =>
      currency
        ? `${formatNumber(amountRange[0] ?? 0, {
            currency: currency as string,
            style: "currency",
            minimumFractionDigits: 0,
          })} - ${
            amountRange[1]
              ? formatNumber(amountRange[1], {
                  currency: currency as string,
                  style: "currency",
                  minimumFractionDigits: 0,
                })
              : "--"
          }`
        : "-- - --",
    [amountRange, currency, formatNumber],
  );
  return { formattedAmountRange, hasAmountFilter };
};
export const useTransactionDateFilter = () => {
  const [startDate, setStartDate] = useState<Date | undefined>(
    transactionSearchManager.timeRangeSelection.get().timeRange === TimeRange.Other
      ? transactionSearchManager.timeRangeSelection.get().bounds[0]?.toDate()
      : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    transactionSearchManager.timeRangeSelection.get().timeRange === TimeRange.Other
      ? transactionSearchManager.timeRangeSelection.get().bounds[1]?.toDate()
      : undefined,
  );

  const onStartDateChange = useCallback(
    (newStartDate: Date | undefined, onlyOneMonth?: boolean) => {
      setStartDate(newStartDate);
      if (!isValidInterval(newStartDate, endDate)) {
        const monthsToAdd = onlyOneMonth ? 1 : 3;
        setEndDate(moment(newStartDate).add(monthsToAdd, "month").endOf("day").toDate());
      }
    },
    [endDate],
  );

  const onEndDateChange = useCallback(
    (newEndDate: Date | undefined, onlyOneMonth?: boolean, preventStartDateChange?: boolean) => {
      setEndDate(newEndDate);

      if (preventStartDateChange) {
        if (!startDate) {
          setStartDate(moment(newEndDate).subtract(1, "month").add(1, "day").startOf("day").toDate());
        }
      } else {
        if (!isValidInterval(startDate, newEndDate)) {
          const monthsToSubtract = onlyOneMonth ? 1 : 3;
          setStartDate(moment(newEndDate).subtract(monthsToSubtract, "month").add(1, "hour").toDate());
        }
      }
    },
    [startDate],
  );
  return { startDate, endDate, onStartDateChange, onEndDateChange };
};

function hasBounds<T>(bounds: [T | null, T | null]) {
  const [low, high] = bounds;
  return low !== null || high !== null;
}

const isValidInterval = (from: Date | undefined, to: Date | undefined) => {
  if (from === undefined && to === undefined) {
    return true;
  }
  if (to === undefined || from === undefined) {
    return false;
  }
  const startDate = moment(from);
  const endDate = moment(to);
  const isStartDateBeforeEndDate = startDate.isSameOrBefore(endDate);
  return isStartDateBeforeEndDate && endDate.diff(startDate, "month", true) <= 3;
};
